import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Loader2, Mic, MicOff, Check } from 'lucide-react';
import { formatNumber, formatKcal } from '../utils/numberFormat';
import { parenteralAPI } from '../services/api';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function AddParenteralDialog({ open, onClose, onAdd }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [isListeningSearch, setIsListeningSearch] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  const handleVoiceSearch = () => {
    if (!SpeechRecognition) {
      alert('Browser Anda tidak mendukung input suara.');
      return;
    }
    if (isListeningSearch) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'id-ID';
    recognition.onstart = () => setIsListeningSearch(true);
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      if (text) {
        setSearch(text.replace(/[.,]/g, '').trim());
      }
      setIsListeningSearch(false);
    };
    recognition.onerror = () => setIsListeningSearch(false);
    recognition.onend = () => setIsListeningSearch(false);
    recognition.start();
  };

  // Manual entry fields
  const [manualName, setManualName] = useState('');
  const [manualEnergy, setManualEnergy] = useState('');
  const [manualProtein, setManualProtein] = useState('');
  const [manualCarbs, setManualCarbs] = useState('');
  const [manualFat, setManualFat] = useState('');

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open]);

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredProducts(products.slice(0, 50));
    } else {
      const filtered = products.filter(product => 
        product.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredProducts(filtered.slice(0, 50));
    }
  }, [search, products]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await parenteralAPI.getAll();
      setProducts(data);
      setFilteredProducts(data.slice(0, 50));
    } catch (error) {
      console.error('Error loading parenteral products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (manualMode) {
      if (!manualName || !manualEnergy) {
        alert('Mohon isi minimal nama produk dan jumlah energi (Kkal)');
        return;
      }

      const qty = parseFloat(quantity) || 1;
      const item = {
        item_id: null,
        name: manualName,
        quantity: qty,
        unit: 'porsi',
        energy_kcal: parseFloat(manualEnergy) * qty,
        protein_g: parseFloat(manualProtein || 0) * qty,
        carbohydrate_g: parseFloat(manualCarbs || 0) * qty,
        fat_g: parseFloat(manualFat || 0) * qty,
        carb_factor: 4.0,  // Default for manual entry
        fat_factor: 9.0,   // Default for manual entry
      };
      onAdd(item);
    } else {
      const qty = parseFloat(quantity) || 0;
      if (!selectedProduct || qty <= 0) {
        alert('Silakan pilih salah satu produk parenteral dan tentukan jumlahnya');
        return;
      }

      const item = {
        item_id: selectedProduct.id,
        name: selectedProduct.name,
        quantity: qty,
        unit: selectedProduct.unit,
        energy_kcal: selectedProduct.macronutrients.energy_kcal * qty,
        protein_g: selectedProduct.macronutrients.protein_g * qty,
        carbohydrate_g: selectedProduct.macronutrients.carbohydrate_g * qty,
        fat_g: selectedProduct.macronutrients.fat_g * qty,
        carb_factor: selectedProduct.factors?.carb_factor || 4.0,
        fat_factor: selectedProduct.factors?.fat_factor || 9.0,
        // Micronutrients - Electrolytes
        sodium_mmol: (selectedProduct.electrolytes?.sodium_mmol || 0) * qty,
        potassium_mmol: (selectedProduct.electrolytes?.potassium_mmol || 0) * qty,
        calcium_mmol: (selectedProduct.electrolytes?.calcium_mmol || 0) * qty,
        chloride_mmol: (selectedProduct.electrolytes?.chloride_mmol || 0) * qty,
        magnesium_mmol: (selectedProduct.electrolytes?.magnesium_mmol || 0) * qty,
        phosphate_mmol: (selectedProduct.electrolytes?.phosphate_mmol || 0) * qty,
        // Other
        lactate_mmol: (selectedProduct.other?.lactate_mmol || 0) * qty,
        acetate_meq: (selectedProduct.other?.acetate_meq || 0) * qty,
        nitrogen_g: (selectedProduct.other?.nitrogen_g || 0) * qty,
      };
      onAdd(item);
    }

    setSelectedProduct(null);
    setQuantity(1);
    setSearch('');
    setManualName('');
    setManualEnergy('');
    setManualProtein('');
    setManualCarbs('');
    setManualFat('');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();

      if (!manualMode && !selectedProduct) {
        if (filteredProducts.length > 0) {
          setSelectedProduct(filteredProducts[0]);
          return;
        } else {
          alert('Please select a product and enter quantity');
          return;
        }
      }

      handleAdd();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">Tambah Nutrisi Parenteral</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Pilih produk cairan / infus parenteral dari database atau masukkan secara manual
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 flex-1 overflow-y-auto">
          {/* Toggle Mode */}
          <div className="flex gap-2">
            <Button
              variant={!manualMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setManualMode(false)}
              className={!manualMode ? 'bg-sky-600 hover:bg-sky-700 text-white text-xs' : 'text-xs text-slate-700'}
            >
              Pilih dari Database
            </Button>
            <Button
              variant={manualMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setManualMode(true)}
              className={manualMode ? 'bg-sky-600 hover:bg-sky-700 text-white text-xs' : 'text-xs text-slate-700'}
            >
              Input Manual
            </Button>
          </div>

          {!manualMode ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  autoFocus
                  placeholder="Ketik nama produk parenteral... (cth: Aminovel 600, Clinimix, Kabiven)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!selectedProduct && filteredProducts.length > 0) {
                        setSelectedProduct(filteredProducts[0]);
                      } else if (selectedProduct) {
                        handleAdd();
                      }
                    }
                  }}
                  className="pl-9 pr-9 h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-sky-500"
                />
                {SpeechRecognition && (
                  <button
                    type="button"
                    onClick={handleVoiceSearch}
                    className={`absolute right-2.5 top-2 p-1 rounded-md transition-colors ${
                      isListeningSearch
                        ? 'text-red-500 bg-red-50 animate-pulse ring-1 ring-red-400'
                        : 'text-slate-400 hover:text-sky-600 hover:bg-slate-100'
                    }`}
                    title={isListeningSearch ? 'Sedang mendengarkan... ucapkan nama produk' : 'Cari dengan suara'}
                  >
                    {isListeningSearch ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                )}
              </div>

              {/* Product List - Enhanced height for 5-6 items */}
              {loading ? (
                <div className="text-center py-12 text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-sky-600" />
                  <p className="text-xs">Memuat database parenteral...</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-y-auto" style={{ height: '250px', minHeight: '250px', maxHeight: '250px' }}>
                  {filteredProducts.map((product, pIdx) => {
                    const isSelected = selectedProduct && (
                      selectedProduct === product ||
                      (selectedProduct.id && product.id && selectedProduct.id === product.id) ||
                      (selectedProduct._id && product._id && selectedProduct._id === product._id) ||
                      (selectedProduct.name === product.name)
                    );

                    return (
                      <div
                        key={product.id || product._id || `${product.name}-${pIdx}`}
                        className={`p-2.5 sm:p-3 border-b cursor-pointer transition-colors flex items-center justify-between gap-3 ${
                          isSelected 
                            ? 'bg-sky-50/90 border-l-4 border-l-sky-600 border-sky-200 shadow-2xs' 
                            : 'hover:bg-slate-50 border-slate-100'
                        }`}
                        onClick={() => setSelectedProduct(product)}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'border-sky-600 bg-sky-600 text-white' : 'border-slate-300 bg-white'
                          }`}>
                            {isSelected && <Check className="h-2.5 w-2.5 stroke-[3]" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <span className={`font-semibold text-xs sm:text-sm truncate ${isSelected ? 'text-sky-950 font-bold' : 'text-slate-900'}`}>
                                {product.name}
                              </span>
                              <span className={`text-[11px] font-bold flex-shrink-0 ml-2 ${isSelected ? 'text-sky-700' : 'text-slate-600'}`}>
                                {formatKcal(product.macronutrients?.energy_kcal || 0)} Kcal
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                              <span>Satuan: <strong>{product.unit}</strong></span>
                              <span>•</span>
                              <span>P: {formatNumber(product.macronutrients?.protein_g || 0)}g</span>
                              <span>•</span>
                              <span>KH: {formatNumber(product.macronutrients?.carbohydrate_g || 0)}g</span>
                              <span>•</span>
                              <span>L: {formatNumber(product.macronutrients?.fat_g || 0)}g</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {filteredProducts.length === 0 && (
                    <div className="text-center py-12 text-slate-400 text-xs">
                      Tidak ada produk parenteral yang cocok dengan "{search}"
                    </div>
                  )}
                </div>
              )}

              {selectedProduct && (
                <div className="bg-sky-50/80 border border-sky-200 p-2.5 sm:p-3 rounded-xl flex-shrink-0">
                  <div className="font-semibold text-xs text-sky-900">
                    Produk Terpilih: {selectedProduct.name}
                  </div>
                  <div className="text-[11px] text-sky-700 mt-0.5">
                    Per {selectedProduct.unit}: {Math.round(selectedProduct.macronutrients.energy_kcal)} Kcal | Protein: {formatNumber(selectedProduct.macronutrients.protein_g)}g | KH: {formatNumber(selectedProduct.macronutrients.carbohydrate_g)}g | Lemak: {formatNumber(selectedProduct.macronutrients.fat_g)}g
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-slate-700">Nama Produk Parenteral *</Label>
                <Input
                  autoFocus
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="cth: Aminovel 600 atau Dextrose 10% 500ml"
                  className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-sky-500 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Energi (Kkal) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={manualEnergy}
                  onChange={(e) => setManualEnergy(e.target.value)}
                  placeholder="cth: 200"
                  className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-sky-500 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Protein / Asam Amino (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={manualProtein}
                  onChange={(e) => setManualProtein(e.target.value)}
                  placeholder="cth: 15.0"
                  className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-sky-500 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Karbohidrat / Dekstrosa (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={manualCarbs}
                  onChange={(e) => setManualCarbs(e.target.value)}
                  placeholder="cth: 50.0"
                  className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-sky-500 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Lemak / Lipid (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={manualFat}
                  onChange={(e) => setManualFat(e.target.value)}
                  placeholder="cth: 0"
                  className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-sky-500 mt-1"
                />
              </div>
            </div>
          )}

          {/* Quantity Section with Quick Chips */}
          <div className="flex-shrink-0 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-slate-700">Jumlah (Botol / Bag / Ampul / Fls)</Label>
              <span className="text-[11px] text-slate-500">
                Total: <strong>{quantity} unit</strong>
              </span>
            </div>
            <Input
              type="number"
              step="0.1"
              min="0.1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="1"
              className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-sky-500 mt-1"
            />
            
            {/* Quick Quantity Chips */}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className="text-[10.5px] font-medium text-slate-400 mr-0.5">Pilihan Cepat:</span>
              {[0.5, 1, 1.5, 2, 3, 4].map((qty) => {
                const isActive = parseFloat(quantity) === qty;
                return (
                  <button
                    key={qty}
                    type="button"
                    onClick={() => setQuantity(qty)}
                    className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-all border ${
                      isActive
                        ? 'bg-sky-600 text-white border-sky-600 shadow-xs'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200'
                    }`}
                  >
                    {qty === 0.5 ? '½ unit' : `${qty} unit`}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} className="text-xs text-slate-600 hover:bg-slate-100 rounded-xl">
            Batal
          </Button>
          <Button 
            onClick={handleAdd}
            className="bg-sky-600 hover:bg-sky-700 text-white font-semibold text-xs rounded-xl shadow-sm"
          >
            Tambahkan ke Recall
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
